import { NO, UIDialogView, UITextView, UIView, YES } from "uicore-ts"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHColors, RHFont, RHRadius, RHShadow, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHCPVSummationReport } from "../../SocketClientFunctions"
import { CPVSummationReportView } from "./CPVSummationReportView"


export interface CPVSummationApprovalRequiredActions {
    keepDraft?: () => void
    editCPVCode?: () => void
    splitIntoParts?: () => void
    requestApproval?: () => void
}


export class CPVSummationApprovalRequiredView extends UIDialogView {
    
    readonly contentView = new UIView()
        .configuredWithObject({
            backgroundColor: RHColors.white,
            style: {
                borderRadius: `${RHRadius.large}px`,
                border: `1px solid ${RHColors.borderSlate.stringValue}`,
                boxShadow: RHShadow.strong
            }
        })
        .addedAsSubviewToView(this.view)
    
    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "CPV-summeerimine vajab kooskõlastust",
            textColor: RHColors.textPrimary
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly explanationLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: NO,
            text: "See on riskimärguanne, mitte automaatne rikkumine. Kokkukuuluvuse lõpliku hinnangu annab hankespetsialist, sest seaduse mõttes sõltub see ka ostu eesmärgist ja funktsionaalsest tervikust. Kui jätkad, märgistab süsteem kirje kooskõlastust nõudvaks.",
            textColor: RHColors.slateDark,
            style: {
                fontSize: RHFont.sizeSmall,
                lineHeight: "1.45"
            }
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly reportView = new CPVSummationReportView()
        .addedAsSubviewToView(this.contentView)
    
    readonly keepDraftButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Salvesta hange",
                style: { fontSize: RHFont.sizeSmall }
            },
            variant: "light",
            contentPadding: RHSpacing.medium
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly editCPVCodeButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Muuda CPV-koodi",
                style: { fontSize: RHFont.sizeSmall }
            },
            variant: "softWarning",
            contentPadding: RHSpacing.medium
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly splitIntoPartsButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Jaga hange osadeks",
                style: { fontSize: RHFont.sizeSmall }
            },
            variant: "softWarning",
            contentPadding: RHSpacing.medium
        })
        .addedAsSubviewToView(this.contentView)
    
    readonly requestApprovalButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: "Võta kooskõlastusele",
                style: { fontSize: RHFont.sizeSmall }
            },
            variant: "success",
            contentPadding: RHSpacing.medium
        })
        .addedAsSubviewToView(this.contentView)
    
    _actions: CPVSummationApprovalRequiredActions = {}
    
    constructor(elementID?: string) {
        super(elementID)
        
        this.view = this.contentView
        this.dismissesOnTapOutside = NO
        
        // noinspection JSConstantReassignment
        this.keepDraftButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.dismiss()
            this._actions.keepDraft?.()
        }
        // noinspection JSConstantReassignment
        this.editCPVCodeButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.dismiss()
            this._actions.editCPVCode?.()
        }
        // noinspection JSConstantReassignment
        this.splitIntoPartsButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.dismiss()
            this._actions.splitIntoParts?.()
        }
        // noinspection JSConstantReassignment
        this.requestApprovalButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.dismiss()
            this._actions.requestApproval?.()
        }
    }
    
    showWithReport(
        report: RHCPVSummationReport,
        actions: CPVSummationApprovalRequiredActions
    ) {
        this._actions = actions
        this.reportView.updateWithReport(report)
        this.showInRootView(YES)
        this.setNeedsLayoutUpToRootView()
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const dialogBounds = this.bounds
        const dialogWidth = Math.min(1080, dialogBounds.width * 0.92)
        const contentWidth = dialogWidth - padding * 3
        const reportHeight = this.reportView.intrinsicContentHeight(contentWidth)
        const explanationHeight = this.explanationLabel.intrinsicContentHeight(contentWidth)
        const buttonHeight = 40
        const contentHeight =
            padding * 5 +
            this.titleLabel.intrinsicContentHeight(contentWidth) +
            explanationHeight +
            reportHeight +
            buttonHeight * 2 +
            RHSpacing.medium
        
        this.contentView.frame = dialogBounds
            .rectangleWithWidth(dialogWidth, 0.5)
            .rectangleWithHeight(Math.min(contentHeight, dialogBounds.height * 0.92), 0.5)
        
        const bounds = this.contentView.contentBoundsWithInset(padding * 1.5)
        
        bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(RHSpacing.small, explanationHeight)
            .assignedAsFrameOfView(this.explanationLabel)
            .rectangleForNextRow(RHSpacing.medium, reportHeight)
            .assignedAsFrameOfView(this.reportView)
            .rectangleForNextRow(RHSpacing.medium, buttonHeight)
            .distributeViewsEquallyAlongWidth(
                [this.keepDraftButton, this.editCPVCodeButton],
                RHSpacing.medium
            )
            .rectangleForNextRow(RHSpacing.medium, buttonHeight)
            .distributeViewsEquallyAlongWidth(
                [this.splitIntoPartsButton, this.requestApprovalButton],
                RHSpacing.medium
            )
    }
    
}
